import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '../hooks/useAuth';
import styles from './LoginPage.module.css';

type Mode = 'login' | 'signup' | 'set-password';

interface InvitePayload {
  /** Present on thread invites */
  sessionKey?: string;
  /** Present on user invites */
  username?: string;
  /** Discriminator: 'user-invite' for user-onboarding tokens, otherwise thread invite */
  type?: string;
  invitedBy?: string;
  exp?: number;
}

/** Decode a JWT payload without verifying — used purely to show the invitee
 *  what they're about to join. Real validation happens on the server. */
function peekInvite(token: string): InvitePayload | null {
  try {
    const body = token.split('.')[1];
    if (!body) return null;
    const padded = body + '='.repeat((4 - (body.length % 4)) % 4);
    const decoded = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

export default function LoginPage() {
  const { login } = useAuth();
  const [mode, setMode] = useState<Mode>('login');

  // Login state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // Signup-only fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [signupUsername, setSignupUsername] = useState('');
  const [signupPassword, setSignupPassword] = useState('');

  // Set-password (admin-issued user invite) fields
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // ?invite=TOKEN handling — preserved across login + signup, used by App.tsx
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [invite, setInvite] = useState<InvitePayload | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('invite');
    if (t) {
      setInviteToken(t);
      const payload = peekInvite(t);
      setInvite(payload);
      // If the JWT body identifies it as a user-invite, show the set-password
      // flow. Otherwise default to signup (existing thread-invite behavior).
      if (payload?.type === 'user-invite') {
        setMode('set-password');
        // Hygiene: strip the user-invite JWT from the address bar / browser
        // history now that it's held in state — the set-password flow never
        // re-reads the URL. (Thread-invite tokens must STAY in the URL: after
        // a plain sign-in, App.tsx re-reads ?invite= to accept the thread and
        // strips it there once consumed.)
        try {
          params.delete('invite');
          const rest = params.toString();
          const clean = window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash;
          window.history.replaceState(null, '', clean);
        } catch {
          /* ignore — cosmetic only */
        }
      } else {
        setMode('signup');
      }
    }
  }, []);

  async function handleLogin(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
      // App.tsx will pick up the ?invite=… in the URL after auth and call accept-invite.
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleSignup(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: signupUsername,
          password: signupPassword,
          name,
          email: email || undefined,
          inviteToken: inviteToken || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Signup failed');
      // Stash the new auth and force a reload so AuthProvider re-bootstraps.
      localStorage.setItem('foreman-auth-token', data.token);
      localStorage.setItem('foreman-auth-user', JSON.stringify(data.user));
      window.location.href = '/';
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Signup failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleSetPassword(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (newPwd !== confirmPwd) {
      setError('Passwords do not match');
      return;
    }
    if (!inviteToken) {
      setError('Missing invite token');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/accept-user-invite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Server prefers the token via Authorization: Bearer; body field kept
          // for belt-and-braces compat (both are accepted).
          Authorization: `Bearer ${inviteToken}`,
        },
        body: JSON.stringify({ token: inviteToken, newPassword: newPwd }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Backend signals this isn't actually a user-invite token — fall back
        // to the thread-invite signup flow rather than dead-ending here.
        const msg = (data.error || '').toString().toLowerCase();
        if (res.status === 400 && (msg.includes('wrong token type') || msg.includes('not a user invite'))) {
          setMode('signup');
          setError('');
          return;
        }
        throw new Error(data.error || 'Could not set password');
      }
      localStorage.setItem('foreman-auth-token', data.token);
      localStorage.setItem('foreman-auth-user', JSON.stringify(data.user));
      window.location.href = '/';
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not set password');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.brand}>HOLLER</h1>
          <p className={styles.sub}>powered by <strong>FOREMAN</strong></p>
        </div>

        {invite && (
          <div className={styles.inviteBanner}>
            <strong>You've been invited{invite.invitedBy ? ` by ${invite.invitedBy}` : ''}.</strong>
            <span>
              {' '}
              {mode === 'set-password'
                ? `Set a password for @${invite.username || 'your account'} to finish setup.`
                : mode === 'signup'
                  ? 'Create an account to join the thread.'
                  : 'Sign in to join.'}
            </span>
          </div>
        )}

        {mode === 'set-password' ? (
          <form onSubmit={handleSetPassword} className={styles.form}>
            <div className={styles.field}>
              <label htmlFor="np-username" className={styles.label}>Username</label>
              <input
                id="np-username"
                type="text"
                readOnly
                value={invite?.username || ''}
                className={styles.input}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="np-password" className={styles.label}>New password</label>
              <input
                id="np-password"
                type="password"
                autoComplete="new-password"
                autoFocus
                required
                minLength={12}
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                className={styles.input}
                placeholder="At least 12 characters"
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="np-confirm" className={styles.label}>Confirm password</label>
              <input
                id="np-confirm"
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                className={styles.input}
                placeholder="Re-enter password"
              />
            </div>
            {error && <div className={styles.error}>{error}</div>}
            <button type="submit" disabled={loading} className={styles.submit}>
              {loading ? 'Setting password\u2026' : 'Set password & sign in'}
            </button>
            <button
              type="button"
              className={styles.modeToggle}
              onClick={() => { setMode('login'); setError(''); }}
            >
              Already have a password? Sign in &rarr;
            </button>
          </form>
        ) : mode === 'login' ? (
          <form onSubmit={handleLogin} className={styles.form}>
            <div className={styles.field}>
              <label htmlFor="username" className={styles.label}>Username</label>
              <input
                id="username"
                type="text"
                autoComplete="username"
                autoFocus
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={styles.input}
                placeholder="Enter username"
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="password" className={styles.label}>Password</label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={styles.input}
                placeholder="Enter password"
              />
            </div>
            {error && <div className={styles.error}>{error}</div>}
            <button type="submit" disabled={loading} className={styles.submit}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <button
              type="button"
              className={styles.modeToggle}
              onClick={() => { setMode('signup'); setError(''); }}
            >
              {inviteToken ? 'New here? Create an account →' : 'Need an account? Sign up →'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSignup} className={styles.form}>
            <div className={styles.field}>
              <label htmlFor="su-name" className={styles.label}>Your name</label>
              <input
                id="su-name"
                type="text"
                autoFocus
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={styles.input}
                placeholder="e.g. Sam Designer"
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="su-email" className={styles.label}>Email <span className={styles.optional}>(optional)</span></label>
              <input
                id="su-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={styles.input}
                placeholder="you@example.com"
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="su-username" className={styles.label}>Pick a username</label>
              <input
                id="su-username"
                type="text"
                autoComplete="username"
                required
                pattern="^[a-z][a-z0-9_-]{1,30}$"
                value={signupUsername}
                onChange={(e) => setSignupUsername(e.target.value.toLowerCase())}
                className={styles.input}
                placeholder="lowercase letters/numbers"
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="su-password" className={styles.label}>Password</label>
              <input
                id="su-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={6}
                value={signupPassword}
                onChange={(e) => setSignupPassword(e.target.value)}
                className={styles.input}
                placeholder="At least 6 characters"
              />
            </div>
            {error && <div className={styles.error}>{error}</div>}
            <button type="submit" disabled={loading} className={styles.submit}>
              {loading ? 'Creating account…' : (inviteToken ? 'Create account & join' : 'Create account')}
            </button>
            <button
              type="button"
              className={styles.modeToggle}
              onClick={() => { setMode('login'); setError(''); }}
            >
              Already have an account? Sign in →
            </button>
          </form>
        )}
      </div>

      <p className={styles.footer}>Kitchens By Woody &middot; Foreman AI</p>
    </div>
  );
}
